import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { ArrowLeft, User, Calendar, Shield, Ban, RotateCcw, Skull, AlertTriangle, Mail, KeyRound, RefreshCw, Unlink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { Avatar } from '../../ui/Avatar';
import { QUERY_KEYS, useUserDetails, useUserWallet, useUpdateUserStatus, usePlayerAutoRefundSummary } from '../../../hooks/useApi';
import { useAuth, usePermission } from '../../../hooks/useAuth';
import FinanceCorrectionDebitModal from '../../finance/FinanceCorrectionDebitModal';
import { useBreadcrumbLabel } from '../../../contexts/BreadcrumbLabelContext';
import { cn } from '../../../utils';
import { useTimezone, formatInTimezone, PageHeader } from '@kioskgaming/ui';
import BanUserModal from './BanUserModal';
import BanStatusCard from './BanStatusCard';
import TerminationRecordCard from './TerminationRecordCard';
import TerminatePlayerModal from './TerminatePlayerModal';
import CloseReleasePlayerModal from './CloseReleasePlayerModal';
import AutoRefundWarningCard from './AutoRefundWarningCard';
import WalletSection from './WalletSection';
import AdminUpdateContactModal from '../../shared/AdminUpdateContactModal';
import AdminSetPasswordModal from '../../shared/AdminSetPasswordModal';
import AdminUpdateUsernameModal from '../../shared/AdminUpdateUsernameModal';
import AdminReissuePlayerCredentialsModal from '../../shared/AdminReissuePlayerCredentialsModal';
import AdminResetGameCredentialsModal from '../../shared/AdminResetGameCredentialsModal';
import { isAdminBanActive, isEffectivelyBanned, isShopBanActive } from '../../../utils/banUtils';
import { toast } from 'react-hot-toast';

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showBanModal, setShowBanModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [showCloseReleaseModal, setShowCloseReleaseModal] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const { hasRole } = useAuth();
  // KIOSK-720: support_admin is view-only for Manage Cashiers/Players — cannot ban/unban.
  const canManageUsers = usePermission('userManagement') && !hasRole('support_admin');
  // KIOSK-720: Adjust Wallet is a write action — operation_admin/platform_admin may only VIEW wallets.
  const canFinanceCorrect = usePermission('walletManagement') && hasRole(['system_admin', 'super_admin', 'finance_admin']);
  const canClearAutoRefundWarning = canManageUsers || canFinanceCorrect;
  const canTerminatePlayer = hasRole(['system_admin', 'super_admin']);
  const canUpdateContact = hasRole(['system_admin', 'super_admin']);
  const canManageCredentials = hasRole(['system_admin', 'super_admin']);
  const canResetGameCredentials = usePermission('gameProviderManagement');
  const updateStatus = useUpdateUserStatus();
  const queryClient = useQueryClient();
  const { timezone } = useTimezone();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [usernameModalOpen, setUsernameModalOpen] = useState(false);
  const [reissueModalOpen, setReissueModalOpen] = useState(false);
  const [gameResetModalOpen, setGameResetModalOpen] = useState(false);

  const { data: userData, isLoading, error, refetch: refetchUser } = useUserDetails(id || '');
  const { data: walletData, isLoading: walletLoading, error: walletError, refetch: refetchWallet } = useUserWallet(id || '');
  const { data: autoRefundData } = usePlayerAutoRefundSummary(id || '');
  const autoRefundWarningActive = autoRefundData?.data?.autoRefundWarning === true;
  const user = userData?.data?.user;
  const breadcrumbPlayerLabel = user
    ? [user.profile?.firstName, user.profile?.lastName].filter(Boolean).join(' ').trim() ||
      user.username?.trim() ||
      user.email
    : undefined;
  useBreadcrumbLabel(breadcrumbPlayerLabel);

  const handleBack = () => {
    navigate('/users');
  };

  const handleUserUpdate = () => {
    // Refresh the page to get updated data
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Player not found</p>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to players
          </Button>
        </div>
      </div>
    );
  }

  const shop = user.shop;
  const agent = shop?.agent;
  const superAgent = agent?.superAgent;

  const effectiveBanned = isEffectivelyBanned(user);
  const shopBanActive = isShopBanActive(user);
  const adminBanActive = isAdminBanActive(user);

  const isTerminated = user.status === 'terminated';

  const formatDateSafe = (value: string | null | undefined) => formatInTimezone(value, timezone);

  const getEffectiveUserStatus = () => {
    if (isTerminated) return 'terminated' as const;
    if (effectiveBanned || user.status === 'banned') return 'banned' as const;
    return user.status;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: 'Active', className: 'bg-green-100 text-green-800', icon: User },
      inactive: { label: 'Inactive', className: 'bg-gray-100 text-gray-800', icon: User },
      suspended: { label: 'Suspended', className: 'bg-yellow-100 text-yellow-800', icon: Ban },
      banned: { label: 'Banned', className: 'bg-red-100 text-red-800', icon: Ban },
      terminated: { label: 'Closed', className: 'bg-stone-100 text-stone-800', icon: Skull },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span className={cn('px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1', config.className)}>
        <Icon className="h-4 w-4" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        back={{ onClick: handleBack, label: 'Back to players' }}
        title="Player details"
        description="View and manage this player's account"
        actions={
          canFinanceCorrect && !isTerminated && id ? (
            <Button
              variant="outline"
              size="sm"
              className="border-destructive/50 text-destructive shrink-0"
              onClick={() => setCorrectionOpen(true)}
            >
              Debit correction
            </Button>
          ) : undefined
        }
      />

      {/* Player summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2" />
            Player
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <Avatar
                user={user}
                size="2xl"
                deterministicColor
              />
              <div className="min-w-0 flex-1 overflow-hidden">
                <h3 className="text-xl sm:text-2xl font-semibold break-words">
                  {user.profile?.firstName} {user.profile?.lastName}
                </h3>
                {user.username?.trim() ? (
                  <p className="text-muted-foreground font-medium break-all">@{user.username.trim()}</p>
                ) : null}
                <p className="text-muted-foreground break-all">{user.email}</p>
                {user.phone ? <p className="text-muted-foreground tabular-nums">{user.phone}</p> : null}
                <div className="flex items-center flex-wrap gap-2 mt-2">
                  {getStatusBadge(getEffectiveUserStatus())}
                  {autoRefundWarningActive ? (
                    <span className="px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 bg-amber-100 text-amber-900 border border-amber-200">
                      <AlertTriangle className="h-4 w-4" />
                      Withdrawals blocked
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {canUpdateContact && !isTerminated ? (
                <Button variant="outline" className="justify-center" onClick={() => setContactModalOpen(true)}>
                  <Mail className="h-4 w-4 mr-2 shrink-0" />
                  Update email / phone
                </Button>
              ) : null}
              {canManageCredentials && !isTerminated && id ? (
                <>
                  <Button variant="outline" className="justify-center" onClick={() => setUsernameModalOpen(true)}>
                    <User className="h-4 w-4 mr-2 shrink-0" />
                    Change username
                  </Button>
                  <Button variant="outline" className="justify-center" onClick={() => setPasswordModalOpen(true)}>
                    <KeyRound className="h-4 w-4 mr-2 shrink-0" />
                    Set password
                  </Button>
                  <Button variant="outline" className="justify-center" onClick={() => setReissueModalOpen(true)}>
                    <RefreshCw className="h-4 w-4 mr-2 shrink-0" />
                    Reissue credentials
                  </Button>
                </>
              ) : null}
              {canResetGameCredentials && !isTerminated && id ? (
                <Button variant="outline" className="justify-center" onClick={() => setGameResetModalOpen(true)}>
                  <KeyRound className="h-4 w-4 mr-2 shrink-0" />
                  Reset game credentials
                </Button>
              ) : null}
              {canManageUsers && !user.banned && user.status === 'suspended' ? (
                <Button
                  variant="outline"
                  className="justify-center"
                  disabled={updateStatus.isLoading}
                  onClick={() => {
                    if (!id || !window.confirm('Reactivate this suspended player?')) return;
                    void updateStatus.mutateAsync({ userId: id, data: { status: 'active' } });
                  }}
                >
                  <RotateCcw className="h-4 w-4 mr-2 shrink-0" />
                  Unsuspend
                </Button>
              ) : null}
              {canManageUsers && !isTerminated ? (
                <Button variant="outline" className="justify-center" onClick={() => setShowBanModal(true)}>
                  {adminBanActive ? (
                    <>
                      <Shield className="h-4 w-4 mr-2 shrink-0" />
                      Remove platform ban
                    </>
                  ) : (
                    <>
                      <Ban className="h-4 w-4 mr-2 shrink-0" />
                      Platform ban
                    </>
                  )}
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between gap-4">
              <span className="text-sm text-muted-foreground shrink-0">Username:</span>
              <span className="text-sm font-medium text-right break-all">
                {user.username?.trim() ? `@${user.username.trim()}` : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Email:</span>
              <span className="text-sm font-medium">{user.email || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Phone:</span>
              <span className="text-sm font-medium tabular-nums">{user.phone || '—'}</span>
            </div>
            <div className="flex justify-between gap-4 items-start">
              <span className="text-sm text-muted-foreground shrink-0 pt-0.5">Shop → Agent → Super agent:</span>
              <div className="text-sm font-medium text-right min-w-0 flex-1">
                {!shop?.id ? (
                  <span>—</span>
                ) : (
                  <div className="flex flex-wrap items-center justify-end gap-x-1 gap-y-1">
                    <Link
                      to={`/shops/${shop.id}`}
                      className="text-primary hover:underline break-all"
                    >
                      {shop.name?.trim() || 'Shop'}
                    </Link>
                    <span className="text-muted-foreground">→</span>
                    {agent?.id ? (
                      <Link
                        to={`/agents/${agent.id}`}
                        className="text-primary hover:underline break-all"
                      >
                        {agent.name?.trim() || 'Agent'}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                    <span className="text-muted-foreground">→</span>
                    {superAgent?.id ? (
                      <Link
                        to={`/super-agents/${superAgent.id}`}
                        className="text-primary hover:underline break-all"
                      >
                        {superAgent.name?.trim() || 'Super agent'}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Role:</span>
              <span className="text-sm font-medium capitalize">{user.profile?.role}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Status:</span>
              {getStatusBadge(getEffectiveUserStatus())}
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Email Verified:</span>
              <span className={`text-sm font-medium ${user.email_verified ? 'text-green-600' : 'text-red-600'}`}>
                {user.email_verified ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Phone Verified:</span>
              <span className={`text-sm font-medium ${user.phone_verified ? 'text-green-600' : 'text-red-600'}`}>
                {user.phone_verified ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Kiosk access:</span>
              <span className={`text-sm font-medium ${effectiveBanned ? 'text-red-600' : 'text-green-600'}`}>
                {effectiveBanned ? 'Blocked' : 'Allowed'}
              </span>
            </div>
          </CardContent>
        </Card>

        <TerminationRecordCard user={user} />

        {/* Ban restrictions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ban restrictions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {effectiveBanned ? (
              <p className="text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2" role="status">
                Player cannot use the kiosk while any restriction below is active.
              </p>
            ) : null}
            <BanStatusCard
              title="Platform ban"
              variant="platform"
              active={adminBanActive}
              reason={user.admin_ban_reason}
              banPeriod={user.admin_ban_period}
              banUntil={user.admin_ban_until}
              bannedAt={user.admin_banned_at}
            />
            <BanStatusCard
              title="Shop ban"
              variant="shop"
              active={shopBanActive}
              reason={user.shop_ban_reason}
              banPeriod={user.shop_ban_period}
              banUntil={user.shop_ban_until}
              bannedAt={user.shop_banned_at}
            />
          </CardContent>
        </Card>

        {id ? (
          <AutoRefundWarningCard userId={id} canClearWarning={canClearAutoRefundWarning} />
        ) : null}

        {/* Account Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Account Timeline</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Created:</span>
              <span className="text-sm flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {formatDateSafe(user.created_at)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Last Updated:</span>
              <span className="text-sm flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {formatDateSafe(user.updated_at)}
              </span>
            </div>
            {user.last_login_at && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Last Login:</span>
                <span className="text-sm flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {formatDateSafe(user.last_login_at)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>


      {canTerminatePlayer && !isTerminated ? (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-lg text-destructive">Danger zone</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Close & release contacts. Does not recall in-game money. Email, phone, username, and
                Telegram are rewritten so the player can register again with the same contacts.
              </p>
              <Button variant="destructive" onClick={() => setShowCloseReleaseModal(true)}>
                <Unlink className="h-4 w-4 mr-2" />
                Close & release contacts
              </Button>
            </div>
            <div className="space-y-3 border-t border-destructive/20 pt-4">
              <p className="text-sm text-muted-foreground">
                Permanent account closure. Cannot be undone. In-game balances are recalled to the main wallet;
                the player cannot log in or transact afterward.
              </p>
              <Button variant="destructive" onClick={() => setShowTerminateModal(true)}>
                <Skull className="h-4 w-4 mr-2" />
                Terminate player
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Wallet Section */}
      <WalletSection
        userId={id || ''}
        wallet={walletData?.data?.wallet}
        stats={walletData?.data?.stats}
        financeStats={walletData?.data?.financeStats}
        isLoading={walletLoading}
        error={walletError}
        accountTerminated={isTerminated}
      />

      {/* Modals */}
      {canUpdateContact && id && !isTerminated ? (
        <AdminUpdateContactModal
          open={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          target={{ kind: 'player', userId: id }}
          currentEmail={user.email}
          currentPhone={user.phone}
          emailVerified={Boolean(user.email_verified)}
          phoneVerified={Boolean(user.phone_verified)}
          title="Update player email / phone"
          onSuccess={() => {
            toast.success('Contact updated. Player must re-verify.');
            void refetchUser();
            void queryClient.invalidateQueries([QUERY_KEYS.USER_DETAILS, id]);
            void queryClient.invalidateQueries(QUERY_KEYS.USERS);
          }}
        />
      ) : null}
      {canManageCredentials && id && !isTerminated ? (
        <>
          <AdminSetPasswordModal
            open={passwordModalOpen}
            onClose={() => setPasswordModalOpen(false)}
            target={{ kind: 'player', userId: id }}
            minLength={8}
            title="Set player password"
            onSuccess={() => {
              toast.success('Password updated. Player sessions were signed out.');
              void refetchUser();
            }}
          />
          <AdminUpdateUsernameModal
            open={usernameModalOpen}
            onClose={() => setUsernameModalOpen(false)}
            target={{ kind: 'player', userId: id }}
            currentUsername={user.username}
            title="Change player username"
            onSuccess={() => {
              toast.success('Username updated. Player sessions were signed out.');
              void refetchUser();
              void queryClient.invalidateQueries([QUERY_KEYS.USER_DETAILS, id]);
              void queryClient.invalidateQueries(QUERY_KEYS.USERS);
            }}
          />
          <AdminReissuePlayerCredentialsModal
            open={reissueModalOpen}
            onClose={() => setReissueModalOpen(false)}
            userId={id}
            currentUsername={user.username}
            onSuccess={() => {
              toast.success('Credentials reissued.');
              void refetchUser();
              void queryClient.invalidateQueries([QUERY_KEYS.USER_DETAILS, id]);
              void queryClient.invalidateQueries(QUERY_KEYS.USERS);
            }}
          />
        </>
      ) : null}
      {canResetGameCredentials && id && !isTerminated ? (
        <AdminResetGameCredentialsModal
          open={gameResetModalOpen}
          onClose={() => setGameResetModalOpen(false)}
          userId={id}
        />
      ) : null}
      <BanUserModal
        user={user}
        isOpen={showBanModal}
        onClose={() => setShowBanModal(false)}
        onSuccess={handleUserUpdate}
      />
      <TerminatePlayerModal
        user={user}
        isOpen={showTerminateModal}
        onClose={() => setShowTerminateModal(false)}
        onSuccess={handleUserUpdate}
      />
      <CloseReleasePlayerModal
        user={user}
        isOpen={showCloseReleaseModal}
        onClose={() => setShowCloseReleaseModal(false)}
        onSuccess={handleUserUpdate}
      />
      {id ? (
        <FinanceCorrectionDebitModal
          open={correctionOpen}
          onOpenChange={setCorrectionOpen}
          ownerType="player"
          ownerId={id}
          onApplied={() => {
            void refetchUser();
            void refetchWallet();
            void queryClient.invalidateQueries([QUERY_KEYS.WALLET, id]);
            void queryClient.invalidateQueries([QUERY_KEYS.USER_DETAILS, id]);
          }}
        />
      ) : null}
    </div>
  );
};

export default UserDetailPage;
